import {
  differenceInCalendarDays,
  differenceInMinutes,
  format,
  formatDistance,
  formatRelative,
  isSameWeek,
  isSameYear,
} from 'date-fns';

import type { Locale } from 'date-fns';

type FormatRelativeDateTimeOptions = {
  /** date-fns locale object */
  locale?: Locale;
  /** Reference date to compare against (defaults to now) */
  referenceDate?: Date;
};

/**
 * Smart relative datetime formatting based on time distance
 *
 * Rules:
 * - If starts in ≤ 60 min → "in X minutes" / "через X хвилин"
 * - If starts today but > 60 min → "today at HH:mm" / "сьогодні о HH:mm"
 * - If yesterday → "yesterday at HH:mm" / "вчора о HH:mm"
 * - If tomorrow → "tomorrow at HH:mm" / "завтра о HH:mm"
 * - If in the same week → "X days ago at HH:mm" / "X днів тому о HH:mm" (past) or "in X days at HH:mm" / "через X днів о HH:mm" (future)
 * - If date in current year → "DD MMM, HH:mm" (21 Nov, 14:30)
 * - If date not in current year → "DD.MM.YYYY HH:mm" (21.11.2026 14:30)
 *
 * @param dateString - Date to format (Date object or ISO string)
 * @param options - Formatting options
 * @param options.locale - date-fns locale object
 * @param options.referenceDate - Reference date to compare against (defaults to now)
 *
 * @returns Formatted date string
 *
 * @example
 * ```ts
 * import { uk } from 'date-fns/locale'
 *
 * // In 30 minutes
 * const date1 = new Date(Date.now() + 30 * 60 * 1000)
 * formatRelativeDateTime(date1, { locale: uk })
 * // => "через 30 хвилин"
 *
 * // In 90 minutes (today)
 * const date2 = new Date(Date.now() + 90 * 60 * 1000)
 * formatRelativeDateTime(date2, { locale: uk })
 * // => "сьогодні о 16:00"
 *
 * // Yesterday
 * const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000)
 * formatRelativeDateTime(yesterday, { locale: uk })
 * // => "вчора о 14:30"
 *
 * // Tomorrow
 * const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000)
 * formatRelativeDateTime(tomorrow, { locale: uk })
 * // => "завтра о 14:30"
 *
 * // 3 days ago (same week)
 * const threeDaysAgo = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)
 * formatRelativeDateTime(threeDaysAgo, { locale: uk })
 * // => "3 дні тому о 14:30"
 *
 * // In 4 days (same week)
 * const fourDaysLater = new Date(Date.now() + 4 * 24 * 60 * 60 * 1000)
 * formatRelativeDateTime(fourDaysLater, { locale: uk })
 * // => "через 4 дні о 14:30"
 *
 * // Same year (different week)
 * const sameYear = new Date('2025-11-21T14:30:00')
 * formatRelativeDateTime(sameYear, { locale: uk })
 * // => "21 лис, 14:30"
 *
 * // Different year
 * const differentYear = new Date('2026-11-21T14:30:00')
 * formatRelativeDateTime(differentYear, { locale: uk })
 * // => "21.11.2026 14:30"
 * ```
 */
export function formatRelativeDateTime(
  dateString: Date | string,
  { locale, referenceDate = new Date() }: FormatRelativeDateTimeOptions = {},
): string {
  const targetDate = typeof dateString === 'string' ? new Date(dateString) : dateString;
  const minutesDiff = differenceInMinutes(targetDate, referenceDate);

  // Rule 1: If starts in ≤ 60 min → use formatDistance
  if (minutesDiff > 0 && minutesDiff <= 60) {
    return formatDistance(targetDate, referenceDate, { addSuffix: true, locale });
  }

  // Rule 2-4: If today, yesterday, or tomorrow → use formatRelative
  const daysDiff = differenceInCalendarDays(targetDate, referenceDate);
  const absDaysDiff = Math.abs(daysDiff);

  if (absDaysDiff <= 1) {
    return formatRelative(targetDate, referenceDate, { locale });
  }

  // Rule 5: If in the same week (2-6 days) → use formatRelative
  if (isSameWeek(targetDate, referenceDate, { locale }) && absDaysDiff >= 2 && absDaysDiff <= 6) {
    return formatRelative(targetDate, referenceDate, { locale });
  }

  // Rule 6: If date in current year → "DD MMM HH:mm"
  if (isSameYear(targetDate, referenceDate)) {
    return format(targetDate, 'd MMM HH:mm', { locale });
  }

  // Rule 7: If date not in current year → "DD.MM.YYYY HH:mm"
  return format(targetDate, 'dd.MM.yyyy HH:mm', { locale });
}
